// Screen 2: Category view
// Primary list = Section Inventory (Deliverable 3) - source of truth per Gemma
// Secondary list = FAQs with matching Parent Section but not in inventory (collapsed)

import { useMemo, useState } from 'react';
import { Box, Typography } from '@/ui';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  getCategoryById,
  getInventoryFaqs,
  getSecondaryFaqs,
} from '../data/supportFaqData';
import type { SupportFaq } from '../data/supportFaqData';

interface CategoryViewProps {
  categoryId: string;
  onBack: () => void;
  onSelectFaq: (faqId: string) => void;
  onSelectCategory: (categoryId: string) => void;
}

const COLORS = {
  accent: '#004b49',
  accentBg: '#e7f5f4',
  borderAccent: '#b8e3df',
  bgCard: '#ffffff',
  border: '#e5e7eb',
  borderSubtle: '#f3f4f6',
  text: '#111827',
  textSecondary: '#4b5563',
  textMuted: '#9ca3af',
  bgSubtle: '#f9fafb',
  shadow: '0 1px 2px 0 rgba(0,0,0,0.04)',
};

function CategoryView({ categoryId, onBack, onSelectFaq, onSelectCategory }: CategoryViewProps) {
  const [showSecondary, setShowSecondary] = useState(false);

  const category = useMemo(() => getCategoryById(categoryId), [categoryId]);
  const primaryFaqs = useMemo(() => getInventoryFaqs(categoryId), [categoryId]);
  const secondaryFaqs = useMemo(() => getSecondaryFaqs(categoryId), [categoryId]);

  if (!category) {
    return (
      <Box data-testid="features-support-faqs-components-category-view-container-action-1" sx={{ textAlign: 'center', py: 6 }}>
        <Typography data-testid="features-support-faqs-components-category-view-text-category-not-found" sx={{ fontSize: '14px', color: COLORS.textSecondary, mb: 2, fontFamily: 'Inter, sans-serif' }}>
          Category not found
        </Typography>
        <Box data-testid="features-support-faqs-components-category-view-container-on-back-1"
          component="button"
          onClick={onBack}
          sx={{
            px: 3,
            py: 1.5,
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: 600,
            backgroundColor: COLORS.accent,
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          Back to Help Centre
        </Box>
      </Box>
    );
  }

  const faqRowSx = (isLast: boolean) => ({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2,
    px: 2.5,
    py: 2,
    textAlign: 'left' as const,
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    borderBottom: isLast ? 'none' : `1px solid ${COLORS.borderSubtle}`,
    transition: 'background-color 0.15s',
    '&:hover': { backgroundColor: COLORS.accentBg },
  });

  return (
    <Box data-testid="features-support-faqs-components-category-view-container-action-2" sx={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Back button + Header */}
      <Box data-testid="features-support-faqs-components-category-view-container-action-3" sx={{ mb: 3 }}>
        <Box data-testid="features-support-faqs-components-category-view-container-on-back-2"
          component="button"
          onClick={onBack}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            mb: 1.5,
            px: 1,
            py: 0.75,
            ml: -1,
            borderRadius: '8px',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            color: COLORS.accent,
            fontSize: '13px',
            fontWeight: 500,
            fontFamily: 'Inter, sans-serif',
            '&:hover': { backgroundColor: COLORS.accentBg },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 18 }} />
          Support &amp; FAQ's
        </Box>
        <Typography data-testid="features-support-faqs-components-category-view-text-action-1"
          sx={{
            fontSize: { xs: '22px', md: '24px' },
            fontWeight: 700,
            color: COLORS.text,
            letterSpacing: '-0.02em',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          {category.title}
        </Typography>
        <Typography data-testid="features-support-faqs-components-category-view-text-action-2"
          sx={{ fontSize: '14px', color: COLORS.textSecondary, mt: 0.25, fontFamily: 'Inter, sans-serif' }}
        >
          {category.subtitle}
        </Typography>
      </Box>

      {/* Primary FAQ List (from Section Inventory) */}
      {primaryFaqs.length > 0 ? (
        <Box data-testid="features-support-faqs-components-category-view-container-action-4"
          sx={{
            borderRadius: '16px',
            overflow: 'hidden',
            border: `1px solid ${COLORS.border}`,
            backgroundColor: COLORS.bgCard,
            boxShadow: COLORS.shadow,
            mb: 3,
          }}
        >
          {primaryFaqs.map((faq: SupportFaq, i) => (
            <Box data-testid="features-support-faqs-components-category-view-container-action-5"
              key={faq.id}
              component="button"
              onClick={() => onSelectFaq(faq.id)}
              sx={faqRowSx(i === primaryFaqs.length - 1)}
            >
              <Typography data-testid="features-support-faqs-components-category-view-text-action-3"
                sx={{
                  flex: 1,
                  fontSize: '14px',
                  fontWeight: 500,
                  color: COLORS.text,
                  lineHeight: 1.4,
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                {faq.title}
              </Typography>
              <ChevronRightIcon sx={{ fontSize: 18, color: COLORS.textMuted, flexShrink: 0 }} />
            </Box>
          ))}
        </Box>
      ) : (
        <Box data-testid="features-support-faqs-components-category-view-container-action-6"
          sx={{
            textAlign: 'center',
            py: 6,
            px: 2,
            borderRadius: '16px',
            backgroundColor: COLORS.bgSubtle,
            border: `1px solid ${COLORS.border}`,
            mb: 3,
          }}
        >
          <Typography data-testid="features-support-faqs-components-category-view-text-no-articles-in-this-category-yet" sx={{ fontSize: '15px', color: COLORS.textSecondary, mb: 1, fontFamily: 'Inter, sans-serif' }}>
            No articles in this category yet
          </Typography>
          <Typography data-testid="features-support-faqs-components-category-view-text-action-4" sx={{ fontSize: '13px', color: COLORS.textMuted, fontFamily: 'Inter, sans-serif' }}>
            Try browsing other categories or searching for your question
          </Typography>
        </Box>
      )}

      {/* Secondary FAQs — collapsed "More FAQs in this topic" */}
      {secondaryFaqs.length > 0 && (
        <Box data-testid="features-support-faqs-components-category-view-container-action-7" sx={{ mb: 5 }}>
          <Box data-testid="features-support-faqs-components-category-view-container-action-8"
            component="button"
            onClick={() => setShowSecondary((v) => !v)}
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              borderRadius: '12px',
              border: `1px solid ${COLORS.border}`,
              backgroundColor: COLORS.bgSubtle,
              cursor: 'pointer',
              fontFamily: 'Inter, sans-serif',
              transition: 'background-color 0.15s',
              '&:hover': { backgroundColor: '#f1f3f5' },
            }}
          >
            <Typography data-testid="features-support-faqs-components-category-view-text-more-faqs-in-this-topic-()"
              sx={{
                fontSize: '13px',
                fontWeight: 600,
                color: COLORS.textSecondary,
                fontFamily: 'Inter, sans-serif',
              }}
            >
              More FAQs in this topic ({secondaryFaqs.length})
            </Typography>
            {showSecondary ? (
              <ExpandLessIcon sx={{ fontSize: 20, color: COLORS.textMuted }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 20, color: COLORS.textMuted }} />
            )}
          </Box>

          {showSecondary && (
            <Box data-testid="features-support-faqs-components-category-view-container-action-9"
              sx={{
                mt: 1,
                borderRadius: '16px',
                overflow: 'hidden',
                border: `1px solid ${COLORS.border}`,
                backgroundColor: COLORS.bgCard,
                boxShadow: COLORS.shadow,
              }}
            >
              {secondaryFaqs.map((faq: SupportFaq, i) => (
                <Box data-testid="features-support-faqs-components-category-view-container-action-10"
                  key={faq.id}
                  component="button"
                  onClick={() => onSelectFaq(faq.id)}
                  sx={faqRowSx(i === secondaryFaqs.length - 1)}
                >
                  <Typography data-testid="features-support-faqs-components-category-view-text-action-5"
                    sx={{
                      flex: 1,
                      fontSize: '14px',
                      fontWeight: 500,
                      color: COLORS.text,
                      lineHeight: 1.4,
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    {faq.title}
                  </Typography>
                  <ChevronRightIcon sx={{ fontSize: 18, color: COLORS.textMuted, flexShrink: 0 }} />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Related from other topics */}
      {category.relatedFromOtherTopics.length > 0 && (
        <Box data-testid="features-support-faqs-components-category-view-container-action-11">
          <Typography data-testid="features-support-faqs-components-category-view-text-related-from-other-topics"
            sx={{
              fontSize: '12px',
              fontWeight: 600,
              color: COLORS.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              mb: 2,
              fontFamily: 'Inter, sans-serif',
            }}
          >
            Related from other topics
          </Typography>
          <Box data-testid="features-support-faqs-components-category-view-container-action-12" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {category.relatedFromOtherTopics.map((rel, idx) => {
              const targetCat = getCategoryById(rel.fromSection);
              const handleClick = () => {
                if (rel.targetFaqId) {
                  onSelectFaq(rel.targetFaqId);
                } else {
                  onSelectCategory(rel.fromSection);
                }
              };
              return (
                <Box data-testid="features-support-faqs-components-category-view-container-handle-click"
                  key={idx}
                  component="button"
                  onClick={handleClick}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    px: 2,
                    py: 1.75,
                    borderRadius: '12px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    backgroundColor: COLORS.bgCard,
                    border: `1px solid ${COLORS.border}`,
                    boxShadow: COLORS.shadow,
                    transition: 'all 0.15s',
                    '&:hover': {
                      backgroundColor: COLORS.accentBg,
                      borderColor: COLORS.borderAccent,
                    },
                  }}
                >
                  <Box data-testid="features-support-faqs-components-category-view-container-action-13" sx={{ flex: 1, minWidth: 0 }}>
                    <Typography data-testid="features-support-faqs-components-category-view-text-action-6"
                      sx={{ fontSize: '14px', fontWeight: 500, color: COLORS.text, fontFamily: 'Inter, sans-serif' }}
                    >
                      {rel.faqTitle}
                    </Typography>
                    <Typography data-testid="features-support-faqs-components-category-view-text-action-7"
                      sx={{ fontSize: '12px', color: COLORS.textMuted, mt: 0.25, fontFamily: 'Inter, sans-serif' }}
                    >
                      {targetCat?.title ?? rel.fromSection}
                    </Typography>
                  </Box>
                  <ChevronRightIcon sx={{ fontSize: 16, color: COLORS.textMuted, flexShrink: 0 }} />
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default CategoryView;
